import type { WizardStep } from '@intelliauth/ui'
import {
  Alert,
  Card,
  Checkbox,
  createLogger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  WizardContainer,
} from '@intelliauth/ui'
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core'
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common'
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en'
import { AlertCircle, Check, Eye, EyeOff, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AuthLayout } from '@/components/AuthLayout'
import { CpV2ApiError, cpClient } from '@/lib/api/cpClient'
import { DOMAIN_BASE } from '@/lib/domain'

// Configure zxcvbn once at module load. The dictionaries + the
// graph model live in data tables; setOptions wires them in. Done at
// module scope so the cost is paid once per tab, not once per
// keystroke.
zxcvbnOptions.setOptions({
  translations: zxcvbnEnPackage.translations,
  graphs: zxcvbnCommonPackage.adjacencyGraphs,
  dictionary: {
    ...zxcvbnCommonPackage.dictionary,
    ...zxcvbnEnPackage.dictionary,
  },
})

const log = createLogger({ component: 'RegisterPage' })

const COMPANY_SIZES = [
  { value: '1-10', label: '1-10' },
  { value: '11-50', label: '11-50' },
  { value: '51-200', label: '51-200' },
  { value: '201-1000', label: '201-1000' },
  { value: '1000+', label: '1000+' },
]

const USE_CASES = [
  { value: 'b2c', label: 'Customer identity (B2C)' },
  { value: 'b2b', label: 'Employee identity (B2B)' },
  { value: 'api', label: 'Partner/API access' },
  { value: 'all', label: 'All of the above' },
]

const RESERVED_SLUGS = new Set([
  'admin',
  'api',
  'auth',
  'billing',
  'dashboard',
  'help',
  'login',
  'logout',
  'platform',
  'register',
  'settings',
  'signup',
  'support',
  'system',
  'tenant',
  'www',
])

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

type Step = 1 | 2 | 3

const WIZARD_STEPS: WizardStep[] = [
  { key: 'account', label: 'Account', description: 'Email + password' },
  { key: 'organization', label: 'Organization', description: 'Name + slug' },
  { key: 'review', label: 'Review', description: 'Confirm details' },
]

interface FieldErrors {
  fullName?: string
  email?: string
  password?: string
  orgName?: string
  orgSlug?: string
  terms?: string
}

type AvailabilityStatus = 'idle' | 'checking' | 'available' | 'taken'

function useDebounce<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])
  return debounced
}

export default function RegisterPage() {
  const [step, setStep] = useState<Step>(1)
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  // Availability check state — ORG SLUG ONLY. Email is intentionally NOT
  // probed: a public "is this email registered?" check is an account-
  // enumeration oracle. The backend closed the /availability/email route
  // and returns a generic ack on signup collision (out-of-band notice to
  // the existing address), so the wire never reveals whether an email is
  // already registered.
  const [slugStatus, setSlugStatus] = useState<AvailabilityStatus>('idle')
  const [slugReason, setSlugReason] = useState<string | null>(null)

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    orgName: '',
    orgSlug: '',
    companySize: '',
    useCase: '',
  })

  const updateField = (field: keyof typeof formData, value: string) => {
    // Clear field error on change
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }))
    setFormData((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'orgName') {
        next.orgSlug = value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')
      }
      return next
    })
    if (field === 'orgName' || field === 'orgSlug') {
      setSlugStatus('idle')
      setSlugReason(null)
    }
  }

  // ── Password strength ────────────────────────────────────────
  //
  // Two evaluations in parallel:
  //
  //   1. Character-class checks — must match the BE rules in
  //      cp/internal/api/handlers/signup.go.checkPasswordStrength.
  //      Drift here is what caused the 12-vs-8 mismatch where the FE
  //      accepted a password the BE rejected.
  //
  //   2. zxcvbn score — semantic strength (catches "Password1!"
  //      types). Score 0-4 maps to Weak/Fair/Good/Strong; we require
  //      the character-class checks AND a zxcvbn score >= 2 to enable
  //      Continue. Pure char-class checks let through dictionary
  //      passwords; zxcvbn catches them.
  const passwordChecks = useMemo(() => {
    const p = formData.password
    return {
      length: p.length >= 8,
      upper: /[A-Z]/.test(p),
      lower: /[a-z]/.test(p),
      digit: /[0-9]/.test(p),
      special: /[^A-Za-z0-9]/.test(p),
    }
  }, [formData.password])

  const zxcvbnResult = useMemo(() => {
    if (!formData.password) return { score: 0, feedback: null as string | null }
    const r = zxcvbn(formData.password)
    return {
      score: r.score, // 0..4
      feedback: r.feedback.warning || r.feedback.suggestions[0] || null,
    }
  }, [formData.password])

  const passwordStrength = useMemo(() => {
    if (!formData.password) return { score: 0, label: '', color: '' }
    // Use zxcvbn score directly for the label/color; the character-
    // class checks drive the per-rule checklist below.
    switch (zxcvbnResult.score) {
      case 0:
        return { score: 0, label: 'Very weak', color: 'bg-red-500' }
      case 1:
        return { score: 1, label: 'Weak', color: 'bg-red-400' }
      case 2:
        return { score: 2, label: 'Fair', color: 'bg-yellow-500' }
      case 3:
        return { score: 3, label: 'Good', color: 'bg-emerald-400' }
      default:
        return { score: 4, label: 'Strong', color: 'bg-emerald-500' }
    }
  }, [formData.password, zxcvbnResult.score])

  const allPasswordCriteriaMet =
    passwordChecks.length &&
    passwordChecks.upper &&
    passwordChecks.lower &&
    passwordChecks.digit &&
    passwordChecks.special &&
    zxcvbnResult.score >= 2

  // ── Debounced availability check (org slug only) ─────────────
  const debouncedSlug = useDebounce(formData.orgSlug, 500)

  // Track latest request to avoid stale responses
  const slugCheckRef = useRef(0)

  useEffect(() => {
    if (!debouncedSlug || debouncedSlug.length < 3 || !SLUG_REGEX.test(debouncedSlug)) {
      setSlugStatus('idle')
      setSlugReason(null)
      return
    }
    if (RESERVED_SLUGS.has(debouncedSlug)) {
      setSlugStatus('taken')
      setSlugReason('is reserved')
      return
    }
    const id = ++slugCheckRef.current
    setSlugStatus('checking')
    // BE-9b: PUBLIC /api/v1/availability/org-slug — anonymous probe
    // matching the email-availability shape. Always 200 with
    // {available: bool}; no auth required so it stays callable on the
    // pre-signup page without tripping the global 401 handler.
    cpClient()
      .get('/api/v1/availability/org-slug', {
        query: { slug: debouncedSlug },
      })
      .then((env) => {
        if (id !== slugCheckRef.current) return
        const data = (env as { data: { available: boolean } }).data
        if (data?.available) {
          setSlugStatus('available')
          setSlugReason(null)
        } else {
          setSlugStatus('taken')
          setSlugReason('already taken')
        }
      })
      .catch(() => {
        if (id !== slugCheckRef.current) return
        setSlugStatus('idle')
        setSlugReason(null)
      })
  }, [debouncedSlug])

  // ── Validation ───────────────────────────────────────────────
  const validateStep1 = useCallback((): boolean => {
    const errs: FieldErrors = {}
    if (!formData.email) {
      errs.email = 'Email is required'
    } else if (!EMAIL_REGEX.test(formData.email)) {
      errs.email = 'Please enter a valid email address'
    }
    if (!formData.password) {
      errs.password = 'Password is required'
    } else if (!allPasswordCriteriaMet) {
      errs.password = 'Password must meet all strength requirements'
    }
    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }, [formData.email, formData.password, allPasswordCriteriaMet])

  const validateStep2 = useCallback((): boolean => {
    const errs: FieldErrors = {}
    if (!formData.orgName) {
      errs.orgName = 'Organization name is required'
    } else if (formData.orgName.length < 2 || formData.orgName.length > 100) {
      errs.orgName = 'Must be between 2 and 100 characters'
    }
    if (!formData.orgSlug) {
      errs.orgSlug = 'Organization slug is required'
    } else if (formData.orgSlug.length < 3 || formData.orgSlug.length > 48) {
      errs.orgSlug = 'Must be between 3 and 48 characters'
    } else if (!SLUG_REGEX.test(formData.orgSlug)) {
      errs.orgSlug = 'Lowercase letters, numbers, and hyphens only'
    } else if (RESERVED_SLUGS.has(formData.orgSlug)) {
      errs.orgSlug = 'This slug is reserved'
    }
    if (!agreedToTerms) {
      errs.terms = 'You must accept the terms'
    }
    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }, [formData.orgName, formData.orgSlug, agreedToTerms])

  const canProceed = () => {
    if (step === 1) {
      return formData.email && allPasswordCriteriaMet
    }
    if (step === 2) {
      return formData.orgName && formData.orgSlug && agreedToTerms && slugStatus !== 'taken'
    }
    return true
  }

  const handleNext = () => {
    if (step === 1 && !validateStep1()) return
    if (step === 2 && !validateStep2()) return
    setStep((step + 1) as Step)
  }

  const handleSubmit = async () => {
    setIsLoading(true)
    setError(null)
    setFieldErrors({})
    log.info('Submitting signup', { orgName: formData.orgName, email: formData.email })

    try {
      await cpClient().post('/api/v1/signup', {
        body: {
          email: formData.email,
          password: formData.password,
          full_name: formData.fullName,
          org_name: formData.orgName,
          org_slug: formData.orgSlug,
        },
      })

      log.info('Signup successful')
      sessionStorage.setItem('justRegistered', '1')
      // Per BE-8: signup returns 202 with no session. User must verify
      // email before logging in. Send them to a "check your email" page.
      window.location.href = '/verify-email/sent?email=' + encodeURIComponent(formData.email)
    } catch (err) {
      if (err instanceof CpV2ApiError) {
        log.debug('Signup error', { status: err.status, code: err.code })
        // CP envelope: `error.details.field_errors: [{field, message}]`.
        const detailsObj = (err.details ?? {}) as {
          field_errors?: Array<{ field?: string; message?: string }>
        }
        const fieldErrs = detailsObj.field_errors ?? []
        if (fieldErrs.length > 0) {
          const errs: FieldErrors = {}
          for (const fe of fieldErrs) {
            if (fe.field === 'email') errs.email = fe.message ?? 'Invalid'
            else if (fe.field === 'password') errs.password = fe.message ?? 'Invalid'
            else if (fe.field === 'org_name') errs.orgName = fe.message ?? 'Invalid'
            else if (fe.field === 'org_slug') errs.orgSlug = fe.message ?? 'Invalid'
          }
          setFieldErrors(errs)
          if (errs.email || errs.password) setStep(1)
          else if (errs.orgName || errs.orgSlug) setStep(2)
        }
        // Email collisions are handled non-enumerably by the backend
        // (generic 202 ack + out-of-band notice to the existing address),
        // so signup never surfaces "this email exists". The only 409 the
        // FE can still see is an org-slug collision, which is not PII.
        if (err.status === 409 && err.message.toLowerCase().includes('slug')) {
          setError('This organization slug is already taken.')
          setStep(2)
        } else if (err.status !== 409) {
          setError(err.message)
        }
      } else {
        setError('Failed to create organization. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // ── Availability indicator component ─────────────────────────
  const AvailabilityIndicator = ({
    status,
    takenMessage,
  }: {
    status: AvailabilityStatus
    takenMessage?: string
  }) => {
    if (status === 'idle') return null
    if (status === 'checking')
      return (
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Checking...
        </span>
      )
    if (status === 'available')
      return (
        <span className="text-xs text-emerald-400 flex items-center gap-1">
          <Check className="h-3 w-3" /> Available
        </span>
      )
    return (
      <span className="text-xs text-red-400 flex items-center gap-1">
        <AlertCircle className="h-3 w-3" /> {takenMessage ?? 'Already taken'}
      </span>
    )
  }

  return (
    <AuthLayout>
      <Card className="p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-foreground">Start your free trial</h2>
          <p className="mt-1.5 text-sm text-muted-foreground">No credit card required</p>
        </div>

        {error && (
          <Alert tone="error" className="mb-6">
            {error}
          </Alert>
        )}

        <WizardContainer
          steps={WIZARD_STEPS}
          currentStep={step - 1}
          onBack={step > 1 ? () => setStep((step - 1) as Step) : undefined}
          onNext={step < 3 ? handleNext : undefined}
          onComplete={step === 3 ? handleSubmit : undefined}
          backLabel="Back"
          nextLabel="Continue"
          completeLabel="Create organization"
          canProceed={Boolean(canProceed())}
          submitting={isLoading}
        >
          {/* ── Step 1: Account Details ─────────────────────── */}
          {step === 1 && (
            <div className="space-y-5 animate-fade-in">
              <div>
                <Label htmlFor="fullName" className="mb-1.5">
                  Full name <span className="text-muted-foreground/60 font-normal">(optional)</span>
                </Label>
                <Input
                  id="fullName"
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => updateField('fullName', e.target.value)}
                  className="h-10"
                  placeholder="Alex Chen"
                />
              </div>

              <div>
                <Label htmlFor="email" className="mb-1.5">
                  Work email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className={`h-10 ${fieldErrors.email ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder="alex@company.com"
                  autoComplete="email"
                />
                {fieldErrors.email && (
                  <p className="mt-1.5 text-sm text-red-400">{fieldErrors.email}</p>
                )}
              </div>

              <div>
                <Label htmlFor="password" className="mb-1.5">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => updateField('password', e.target.value)}
                    className={`h-10 pr-10 ${fieldErrors.password ? 'border-red-400 focus:ring-red-400' : ''}`}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p className="mt-1.5 text-sm text-red-400">{fieldErrors.password}</p>
                )}

                {/* Password Strength */}
                {formData.password && (
                  <div className="mt-3">
                    <div className="flex gap-1 mb-2">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded-full ${
                            i <= passwordStrength.score
                              ? passwordStrength.color
                              : 'bg-foreground/10'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Password strength:{' '}
                      <span className="font-medium">{passwordStrength.label}</span>
                    </p>
                    <div className="mt-2 space-y-1">
                      {[
                        { test: passwordChecks.length, label: '8+ characters' },
                        { test: passwordChecks.upper, label: 'Uppercase letter' },
                        { test: passwordChecks.lower, label: 'Lowercase letter' },
                        { test: passwordChecks.digit, label: 'Number' },
                        { test: passwordChecks.special, label: 'Special character' },
                      ].map((req) => (
                        <div key={req.label} className="flex items-center gap-2 text-xs">
                          <div
                            className={`w-3.5 h-3.5 rounded-full flex items-center justify-center ${req.test ? 'bg-emerald-500' : 'bg-foreground/10'}`}
                          >
                            {req.test && <Check className="h-2.5 w-2.5 text-white" />}
                          </div>
                          <span
                            className={
                              req.test ? 'text-muted-foreground' : 'text-muted-foreground/60'
                            }
                          >
                            {req.label}
                          </span>
                        </div>
                      ))}
                    </div>
                    {/* zxcvbn semantic feedback — surfaces things the
                        per-rule checklist can't, e.g. "This is a
                        commonly used password." Only shown when the
                        score is sub-Fair AND zxcvbn has something to
                        say; once the user clears Fair we stop nagging. */}
                    {zxcvbnResult.feedback && zxcvbnResult.score < 2 && (
                      <p className="mt-2 text-xs text-amber-500">{zxcvbnResult.feedback}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Step 2: Organization Details ────────────────── */}
          {step === 2 && (
            <div className="space-y-5 animate-fade-in">
              <div>
                <Label htmlFor="orgName" className="mb-1.5">
                  Organization name
                </Label>
                <Input
                  id="orgName"
                  type="text"
                  value={formData.orgName}
                  onChange={(e) => updateField('orgName', e.target.value)}
                  className={`h-10 ${fieldErrors.orgName ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder="Acme Corporation"
                />
                {fieldErrors.orgName && (
                  <p className="mt-1.5 text-sm text-red-400">{fieldErrors.orgName}</p>
                )}
              </div>

              <div>
                <Label htmlFor="orgSlug" className="mb-1.5">
                  Organization slug
                </Label>
                <div className="flex">
                  <Input
                    id="orgSlug"
                    type="text"
                    value={formData.orgSlug}
                    onChange={(e) => {
                      const cleaned = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')
                      setFormData((prev) => ({ ...prev, orgSlug: cleaned }))
                      setFieldErrors((prev) => ({ ...prev, orgSlug: undefined }))
                      setSlugStatus('idle')
                      setSlugReason(null)
                    }}
                    className={`h-10 rounded-r-none border-r-0 flex-1 ${fieldErrors.orgSlug ? 'border-red-400 focus:ring-red-400' : ''}`}
                    placeholder="acme"
                  />
                  <span className="inline-flex items-center px-3 border border-input border-l-0 rounded-r-md bg-foreground/5 text-muted-foreground text-sm">
                    .{DOMAIN_BASE}
                  </span>
                </div>
                <div className="mt-1.5 flex items-center justify-between">
                  {fieldErrors.orgSlug ? (
                    <p className="text-sm text-red-400">{fieldErrors.orgSlug}</p>
                  ) : (
                    <AvailabilityIndicator
                      status={slugStatus}
                      takenMessage={slugReason ?? 'Already taken'}
                    />
                  )}
                </div>
              </div>

              {/* URL Preview */}
              {formData.orgSlug &&
                formData.orgSlug.length >= 3 &&
                SLUG_REGEX.test(formData.orgSlug) && (
                  <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      Your tenants will be accessible at{' '}
                      <span className="font-bold text-foreground">
                        {`<tenant>-${formData.orgSlug}.${DOMAIN_BASE}`}
                      </span>
                    </p>
                  </div>
                )}

              <div>
                <Label htmlFor="companySize" className="mb-1.5">
                  Company size
                </Label>
                <Select
                  value={formData.companySize}
                  onValueChange={(value) => updateField('companySize', value)}
                >
                  <SelectTrigger id="companySize" className="h-10">
                    <SelectValue placeholder="Select company size" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_SIZES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="useCase" className="mb-1.5">
                  Primary use case{' '}
                  <span className="text-muted-foreground/60 font-normal">(optional)</span>
                </Label>
                <Select
                  value={formData.useCase}
                  onValueChange={(value) => updateField('useCase', value)}
                >
                  <SelectTrigger id="useCase" className="h-10">
                    <SelectValue placeholder="Select use case" />
                  </SelectTrigger>
                  <SelectContent>
                    {USE_CASES.map((u) => (
                      <SelectItem key={u.value} value={u.value}>
                        {u.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Terms */}
              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={agreedToTerms}
                    onCheckedChange={(checked) => {
                      setAgreedToTerms(checked === true)
                      setFieldErrors((prev) => ({ ...prev, terms: undefined }))
                    }}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-muted-foreground">
                    I agree to the{' '}
                    <a href="#" className="text-primary hover:text-primary/80">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="#" className="text-primary hover:text-primary/80">
                      Privacy Policy
                    </a>
                  </span>
                </label>
                {fieldErrors.terms && (
                  <p className="mt-1.5 text-sm text-red-400">{fieldErrors.terms}</p>
                )}
              </div>
            </div>
          )}

          {/* ── Step 3: Review ─────────────────────────────── */}
          {step === 3 && (
            <div className="space-y-4 animate-fade-in">
              {/* Account Summary */}
              <div className="bg-foreground/5 border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-foreground">Account</h4>
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="text-xs text-primary hover:text-primary/80 font-medium"
                  >
                    Edit
                  </button>
                </div>
                <div className="space-y-2 text-sm">
                  {formData.fullName && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Name</span>
                      <span className="text-foreground font-medium">{formData.fullName}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Email</span>
                    <span className="text-foreground font-medium">{formData.email}</span>
                  </div>
                </div>
              </div>

              {/* Organization Summary */}
              <div className="bg-foreground/5 border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-foreground">Organization</h4>
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="text-xs text-primary hover:text-primary/80 font-medium"
                  >
                    Edit
                  </button>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Name</span>
                    <span className="text-foreground font-medium">{formData.orgName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">URL</span>
                    <span className="text-foreground font-bold font-mono text-xs">
                      {formData.orgSlug}.{DOMAIN_BASE}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Plan</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary/20 text-primary text-xs font-medium">
                      Free
                    </span>
                  </div>
                </div>
              </div>

              {/* What's next */}
              <div className="bg-foreground/5 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-foreground mb-3">What happens next</h4>
                <ul className="space-y-2">
                  {[
                    'Your organization will be created instantly',
                    'You can create tenants and invite team members',
                    'Configure authentication flows and go live',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </WizardContainer>

        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium text-primary hover:text-primary/80 transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </Card>
    </AuthLayout>
  )
}
